import { Passenger, Reservation, Segment } from '@app/models/micro';
import {
  GetPassengerDataRequestInt,
  GetPassengerDataResponseInt,
  PassengerDataPassengerRequestInt,
  PassengerDataResponsePassengerInt,
} from '@app/interfaces/micro/passenger-data.interface';
import moment from 'moment';

/**
 * Mapper para el microservicio getPassengerData
 * Se encarga de transformar los datos de la reservación al formato
 * requerido por el microservicio y de mapear la respuesta
 */
export class PassengerDataMapper {
  /**
   * Construye el request para el endpoint del BFF {versionCheckin}/kiosk/checkin/getPassengerData
   * @param reservation - Reservación actual
   * @param passengers - Lista de pasajeros a consultar
   * @returns Request formateado para el microservicio
   */
  static buildRequest(
    reservation: Reservation,
    passengers: Passenger[]
  ): GetPassengerDataRequestInt {
    return {
      reservationCode: reservation.pnr,
      segment: {
        departureAirport: reservation.flightSummary.currentSegment.departureAirport,
        departureDate: moment(reservation.flightSummary.currentSegment.departureDateTime).format(
          'YYYY-MM-DD'
        ),
        operatingCarrier: reservation.flightSummary.currentSegment.operatingCarrier,
        operatingFlightCode: reservation.flightSummary.currentSegment.operatingFlightCode,
      },
      passengers: this.mapPassengersToRequest(passengers),
    };
  }

  /**
   * Mapea la lista de pasajeros al formato del request
   * @param passengers - Lista de pasajeros
   * @returns Lista de pasajeros formateada para el request
   */
  private static mapPassengersToRequest(
    passengers: Passenger[]
  ): PassengerDataPassengerRequestInt[] {
    return passengers
      .filter((passenger) => passenger.passengerInfo?.type !== 'INF')
      .map((passenger) => ({
        firstName: passenger.firstName,
        lastName: passenger.lastName,
        type: passenger.passengerInfo?.type || '',
        passengerNameNumber: passenger.nameRefNumber,
      }));
  }

  /**
   * Cuenta las maletas documentadas (INACTIVE) para un pasajero específico
   * basándose en el passengerNameNumber
   * @param response - Respuesta del microservicio
   * @param passengerNameNumber - Número de referencia del pasajero
   * @returns Cantidad de maletas documentadas
   */
  static getDocumentedBagsCount(
    response: GetPassengerDataResponseInt,
    passengerNameNumber: string
  ): number {
    const passenger = response.passengers.find(
      (p) => p.passengerNameNumber === passengerNameNumber
    );

    if (!passenger || !passenger.passengerItinerary?.length) {
      return 0;
    }

    // Suma todas las maletas documentadas de todos los itinerarios
    return passenger.passengerItinerary.reduce((total, itinerary) => {
      const documentedBags = itinerary.bagTagInfo?.length || 0;
      return total + documentedBags;
    }, 0);
  }

  /**
   * Verifica si la respuesta tiene errores o warnings
   * @param response - Respuesta del microservicio
   * @returns true si hay warnings/errores
   */
  static hasErrors(response: GetPassengerDataResponseInt): boolean {
    return response.warnings && response.warnings.length > 0;
  }

  /**
   * Obtiene el mensaje de error de la respuesta
   * @param response - Respuesta del microservicio
   * @returns Mensaje de error o null
   */
  static getErrorMessage(response: GetPassengerDataResponseInt): string | null {
    if (this.hasErrors(response)) {
      return response.warnings[0]?.errorMessage || null;
    }
    return null;
  }

  /**
   * Obtiene los datos de un pasajero específico de la respuesta
   * @param response - Respuesta del microservicio
   * @param passengerNameNumber - Número de referencia del pasajero
   * @returns Datos del pasajero o undefined
   */
  static getPassengerFromResponse(
    response: GetPassengerDataResponseInt,
    passengerNameNumber: string
  ): PassengerDataResponsePassengerInt | undefined {
    return response.passengers.find((p) => p.passengerNameNumber === passengerNameNumber);
  }
}
